import React, { useEffect, useState } from "react";
import { Reveal } from "@/hooks/useScrollReveal";
import { Mail, Phone, MapPin, Send, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useSiteContent, interpolateTemplate } from "@/context/SiteContentContext";
import { DEFAULT_SITE_CONTENT } from "@/lib/defaultSiteContent";
import { SITE_CONTAINER_FORM } from "@/lib/siteLayout";

export const Contact = ({ prefillCar, onPrefillConsumed }) => {
  const { content } = useSiteContent();
  const c = content.contact || DEFAULT_SITE_CONTENT.contact;
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (prefillCar) {
      setForm((f) => ({
        ...f,
        message: interpolateTemplate(c.prefill_template, {
          make: prefillCar.make,
          model: prefillCar.model,
          year: prefillCar.year,
        }),
      }));
      document.getElementById("kapcsolat")?.scrollIntoView({ behavior: "smooth" });
      onPrefillConsumed?.();
    }
  }, [prefillCar]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/inquiries", {
        ...form,
        car_id: prefillCar?.id || null,
        car_label: prefillCar ? `${prefillCar.make} ${prefillCar.model}` : null,
      });
      setSent(true);
      toast.success(c.toast_success);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (e) {
      toast.error(c.toast_error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="kapcsolat" data-testid="contact-section" className="relative py-24">
      <div className={`${SITE_CONTAINER_FORM} grid grid-cols-1 lg:grid-cols-2 gap-12`}>
        <Reveal>
          <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-electric mb-3">
            <Mail className="w-4 h-4" strokeWidth={1.5} /> {c.eyebrow}
          </div>
          <h2 className="font-heading text-3xl md:text-4xl font-light tracking-tight">
            {c.title}
          </h2>
          <p className="text-slate-400 mt-3 leading-relaxed">
            {c.description}
          </p>

          <div className="mt-8 space-y-4">
            {(c.channels || []).map((ch, i) => {
              const Icon = [Phone, Mail, MapPin][i] || Mail;
              return (
              <div key={ch.label} className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-full glass flex items-center justify-center">
                  <Icon className="w-4 h-4 text-electric" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-400">{ch.label}</div>
                  <div className="text-slate-200">{ch.value}</div>
                </div>
              </div>
            );
            })}
          </div>
        </Reveal>

        <Reveal delay={150}>
          <form onSubmit={submit} data-testid="contact-form" className="glass rounded-3xl p-8">
            {sent ? (
              <div data-testid="contact-success" className="flex flex-col items-center justify-center text-center py-12">
                <CheckCircle2 className="w-14 h-14 text-electric mb-4" strokeWidth={1.5} />
                <h3 className="font-heading text-2xl font-light">{c.success_title}</h3>
                <p className="text-slate-400 mt-2">{c.success_body}</p>
                <button
                  type="button"
                  onClick={() => setSent(false)}
                  className="mt-6 text-sm text-electric hover:text-electric-light"
                >
                  {c.success_again_label}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <Field testid="contact-name" label={c.form_name_label} value={form.name}
                  onChange={(v) => setForm({ ...form, name: v })} required />
                <Field testid="contact-email" label={c.form_email_label} type="email" value={form.email}
                  onChange={(v) => setForm({ ...form, email: v })} required />
                <Field testid="contact-phone" label={c.form_phone_label} value={form.phone}
                  onChange={(v) => setForm({ ...form, phone: v })} />
                <div>
                  <label className="text-xs tracking-[0.15em] uppercase text-slate-400 block mb-2">{c.form_message_label}</label>
                  <textarea
                    data-testid="contact-message"
                    required
                    rows={4}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="w-full bg-white/[0.03] border border-white/10 rounded-xl p-3 text-sm text-white placeholder:text-slate-500 focus:border-electric outline-none transition-colors resize-none"
                  />
                </div>
                <button
                  data-testid="contact-submit"
                  type="submit"
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-full bg-electric hover:bg-electric-light disabled:opacity-50 text-white font-medium transition-all hover:blue-glow"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" strokeWidth={1.5} />}
                  {c.submit_label}
                </button>
              </div>
            )}
          </form>
        </Reveal>
      </div>
    </section>
  );
};

const Field = ({ testid, label, value, onChange, type = "text", required }) => (
  <div>
    <label className="text-xs tracking-[0.15em] uppercase text-slate-400 block mb-2">{label}</label>
    <input
      data-testid={testid}
      type={type}
      required={required}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-white/[0.03] border border-white/10 rounded-xl p-3 text-sm text-white focus:border-electric outline-none transition-colors"
    />
  </div>
);
